// Centered window with a title bar and a close button. Escape also closes.
// Focus moves into the window when it opens, Tab stays inside it, and focus
// returns to where it was when the window closes. With several windows open,
// only the top one reacts to keys.
import { useEffect, useId, useRef, type ReactNode } from "react";
import { Icon } from "./Icon";

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  className?: string;
  /** Extra class on the dim background (e.g. to stack above other layers). */
  backdropClassName?: string;
  /** Clicking the dim background closes the window (default true). */
  closeOnBackdrop?: boolean;
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/** Open windows, oldest first; the last one is on top. */
const openStack: symbol[] = [];

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.closest("[hidden], [inert]"));
}

/** Keep Tab / Shift+Tab inside `dialog`. */
export function trapTab(e: KeyboardEvent, dialog: HTMLElement): void {
  const items = focusables(dialog);
  const active = document.activeElement;
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) {
    e.preventDefault();
    dialog.focus();
    return;
  }
  const inside = active instanceof Node && dialog.contains(active);
  if (e.shiftKey) {
    if (!inside || active === first || active === dialog) {
      e.preventDefault();
      last.focus();
    }
  } else if (!inside || active === last) {
    e.preventDefault();
    first.focus();
  }
}

export function Modal({ title, onClose, children, testId, className, backdropClassName, closeOnBackdrop = true }: Props) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const me = Symbol("modal");
    openStack.push(me);
    // Remember the opener, then move focus to the window itself (not to a
    // button: Enter must not trigger an action the player has not read yet).
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();

    const onKey = (e: KeyboardEvent) => {
      if (openStack[openStack.length - 1] !== me) return;
      if (e.key === "Escape") onCloseRef.current();
      else if (e.key === "Tab" && dialogRef.current) trapTab(e, dialogRef.current);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = openStack.indexOf(me);
      if (i >= 0) openStack.splice(i, 1);
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return (
    <div
      className={`modal-backdrop ${backdropClassName ?? ""}`}
      onPointerDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`modal ${className ?? ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
      >
        <header className="modal-head titlebar">
          <span className="led" aria-hidden="true" />
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="tb-btn close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={14} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
